/**
 * PaginationManager — handles client-side and server-side pagination
 * for Argus Table.
 *
 * In client mode, slices a dataset into pages and tracks navigation state.
 * In server mode, tracks page position and exposes a `hasMore` flag so the
 * parent controller can manage DynamoDB pagination tokens externally.
 */
export class PaginationManager {
  /**
   * @param {Object}  options
   * @param {number}  [options.pageSize=50]           - Rows per page
   * @param {Array}   [options.pageList=[10,25,50,100]] - Selectable page sizes
   * @param {boolean} [options.serverPagination=false] - Enable server pagination mode
   */
  constructor({ pageSize = 50, pageList = [10, 25, 50, 100], serverPagination = false } = {}) {
    this._pageSize = pageSize
    this._pageList = pageList
    this._serverPagination = serverPagination
    this._currentPage = 1
    this._hasMore = false
  }

  /**
   * The current page number (1-based).
   *
   * @returns {number}
   */
  get currentPage() {
    return this._currentPage
  }

  /**
   * The current page size.
   *
   * @returns {number}
   */
  get pageSize() {
    return this._pageSize
  }

  /**
   * The list of selectable page sizes.
   *
   * @returns {Array<number>}
   */
  get pageList() {
    return this._pageList
  }

  /**
   * Whether server pagination mode is enabled.
   *
   * @returns {boolean}
   */
  get serverPagination() {
    return this._serverPagination
  }

  /**
   * Whether more pages are available (server pagination only).
   *
   * @returns {boolean}
   */
  get hasMore() {
    return this._hasMore
  }

  /**
   * Set whether more pages are available (server pagination).
   *
   * @param {boolean} hasMore
   */
  setHasMore(hasMore) {
    this._hasMore = Boolean(hasMore)
  }

  /**
   * Compute the current page slice from a dataset.
   *
   * Clamps the current page to the valid range [1, totalPages] before
   * slicing. For empty datasets, returns a zeroed-out result with
   * currentPage = 1.
   *
   * @param {Object[]} rows - Full (filtered + sorted) dataset
   * @returns {{ pageRows: Object[], totalRows: number, from: number, to: number, currentPage: number, totalPages: number }}
   */
  paginate(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
      this._currentPage = 1
      return {
        pageRows: [],
        totalRows: 0,
        from: 0,
        to: 0,
        currentPage: 1,
        totalPages: 0,
      }
    }

    const totalRows = rows.length
    const totalPages = Math.ceil(totalRows / this._pageSize)

    // Clamp current page to valid range
    if (this._currentPage < 1) {
      this._currentPage = 1
    }
    if (this._currentPage > totalPages) {
      this._currentPage = totalPages
    }

    const from = (this._currentPage - 1) * this._pageSize + 1
    const to = Math.min(this._currentPage * this._pageSize, totalRows)
    const pageRows = rows.slice(from - 1, to)

    return {
      pageRows,
      totalRows,
      from,
      to,
      currentPage: this._currentPage,
      totalPages,
    }
  }

  /**
   * Navigate to a specific page.
   *
   * The page is stored directly; clamping happens in `paginate()` when
   * the actual dataset length is known.
   *
   * @param {number} page - Target page number (1-based)
   */
  goToPage(page) {
    const p = Number(page)

    if (!Number.isFinite(p) || p < 1) {
      this._currentPage = 1
    } else {
      this._currentPage = Math.floor(p)
    }
  }

  /**
   * Advance to the next page.
   */
  nextPage() {
    this._currentPage += 1
  }

  /**
   * Go back to the previous page (minimum page 1).
   */
  prevPage() {
    if (this._currentPage > 1) {
      this._currentPage -= 1
    }
  }

  /**
   * Update the page size and reset to page 1.
   *
   * @param {number} size - New page size
   */
  setPageSize(size) {
    const s = Number(size)

    if (Number.isFinite(s) && s > 0) {
      this._pageSize = Math.floor(s)
    }

    this._currentPage = 1
  }
}
